import { useEffect, useState } from 'react';
import TransferForm, { DEFAULT_DATA, k_transfer_form_data } from '@components/transfer/TransferForm';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { Selector } from '@utils/constants';
import ActivitiesGrid from '@components/activity/ActivitiesGrid';
import { Alert, Button, Modal, Result, message } from 'antd';
import useTransferRedirect, { RedirectRequest, RedirectResult } from '@hooks/transfer-enquiry/useTransferRedirect';
import useTransferEligibility from '@hooks/transfer-enquiry/useTransferEligibility';
import useCreateTransferEnquiry from '@hooks/transfer-enquiry/useCreateTransferEnquiry';
import useRequestTransferAccess from '@hooks/transfer-enquiry/useRequestTransferAccess';

// Form data shape is defined in the JS TransferForm; we mirror the fields the
// redirect endpoint consumes here. Extra keys (placeIDs, returnTrip flag) are
// ignored by the backend and don't need to be typed.
type FormData = RedirectRequest & {
    pickupPlaceID?: string;
    dropoffPlaceID?: string;
    returnTrip?: string;
};

interface ActivityItem {
    selectors?: string[];
    selectorItems: { name: string; sort: number }[];
}

interface RootState {
    activities: { data: ActivityItem[]; fetching: boolean };
}

function TransferEnquiryHomePage() {
    const jsonString = sessionStorage.getItem(k_transfer_form_data);
    const initData: FormData = jsonString ? JSON.parse(jsonString) : (DEFAULT_DATA as FormData);
    const [data, setData] = useState<FormData>(initData);
    const activities = useSelector((state: RootState) => state.activities.data);
    const fetching = useSelector((state: RootState) => state.activities.fetching);
    const [transferActivties, setTransferActivties] = useState<ActivityItem[]>([]);
    const { building, buildRedirect } = useTransferRedirect();
    const { state: eligibility } = useTransferEligibility();
    const { submitting: creatingEnquiry, create: createEnquiry } = useCreateTransferEnquiry();
    const { submitting: requestingAccess, request: requestAccess } = useRequestTransferAccess();

    const navigate = useNavigate();

    const [result, setResult] = useState<RedirectResult | undefined>();
    const [enquiryResult, setEnquiryResult] = useState<{ enquiryId: string } | undefined>();
    const [confirmOpen, setConfirmOpen] = useState(false);
    // Locks the 申请开通 button after a click, regardless of debounce, to
    // suppress local hammering. Reset only on full page reload.
    const [accessRequested, setAccessRequested] = useState(false);

    const isEligible = eligibility.kind === 'eligible';
    const submitButtonLabel = isEligible ? '确认提交' : '提交询价';
    const confirmOkLabel = isEligible ? '确认并前往预订' : '确认提交询价';
    const submitting = building || creatingEnquiry;

    useEffect(() => {
        const j = sessionStorage.getItem(k_transfer_form_data);
        if (j) setData(JSON.parse(j));
    }, []);

    useEffect(() => {
        const fixedSelector = Selector.Transfers;
        const newData = activities.filter((item) => item.selectors?.includes(fixedSelector));
        newData.sort((a, b) => {
            const sortA = a.selectorItems.find((item) => item.name == fixedSelector)?.sort ?? 0;
            const sortB = b.selectorItems.find((item) => item.name == fixedSelector)?.sort ?? 0;
            return sortA - sortB;
        });
        setTransferActivties(newData);
    }, [activities]);

    useEffect(() => {
        sessionStorage.setItem(k_transfer_form_data, JSON.stringify(data));
    }, [data]);

    const handleSubmit = () => {
        setConfirmOpen(true);
    };

    const handleConfirm = async () => {
        // Non-eligible flow: skip the redirect entirely and create an enquiry.
        // Eligibility is pre-flighted on mount; if Mozio drops between page
        // load and submit the redirect path's existing fallback covers us.
        if (!isEligible) {
            try {
                const res = await createEnquiry({
                    date: data.date,
                    time: data.time,
                    retDate: data.retDate,
                    retTime: data.retTime,
                    pickup: data.pickup,
                    dropoff: data.dropoff,
                    hours: data.hours,
                    passengerCount: data.passengerCount,
                    luggageCount: data.luggageCount,
                });
                setEnquiryResult({ enquiryId: res.enquiryId });
                setConfirmOpen(false);
            } catch (e) {
                const msg = e instanceof Error ? e.message : String(e);
                console.error('Error creating transfer enquiry:', e);
                message.error(`提交失败: ${msg}`);
                setConfirmOpen(false);
            }
            return;
        }

        // Eligible flow: pre-open a blank tab inside the user-gesture window so
        // popup blockers allow it. The modal's OK click is itself a fresh user
        // gesture, so window.open here is still treated as user-initiated.
        // Assign the URL after the API returns; close on any other branch.
        const popup = window.open('about:blank', '_blank');

        try {
            const res = await buildRedirect({ ...data });
            switch (res.kind) {
                case 'redirect':
                    if (popup) {
                        popup.location.href = res.redirectUrl;
                    } else {
                        window.location.href = res.redirectUrl;
                    }
                    setResult(res);
                    setConfirmOpen(false);
                    return;
                case 'fallback':
                    if (popup) popup.close();
                    setResult(res);
                    setConfirmOpen(false);
                    return;
                case 'not_provisioned':
                case 'agency_not_enabled':
                case 'not_configured':
                    if (popup) popup.close();
                    setResult(res);
                    setConfirmOpen(false);
                    return;
                default:
                    if (popup) popup.close();
                    message.error('提交失败，请重试');
                    setConfirmOpen(false);
            }
        } catch (e) {
            if (popup) popup.close();
            const msg = e instanceof Error ? e.message : String(e);
            console.error('Error submitting transfer request:', e);
            message.error(`提交失败: ${msg}`);
            setConfirmOpen(false);
        }
    };

    const handleRequestAccess = async () => {
        if (accessRequested || requestingAccess) return;
        try {
            await requestAccess();
            setAccessRequested(true);
            message.success('申请已提交，管理员将尽快为您开通。');
        } catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            console.error('Error requesting transfer access:', e);
            message.error(`申请提交失败: ${msg}`);
        }
    };

    const Success = () => {
        if (!result) return null;

        if (result.kind === 'redirect') {
            return (
                <Result
                    status="success"
                    title="已跳转至预订页面"
                    subTitle="如果新标签页未自动打开，请检查浏览器弹窗拦截设置后重试。"
                    extra={[
                        <Button key="back" onClick={() => setResult(undefined)}>
                            返回询价页面
                        </Button>,
                    ]}
                />
            );
        }

        if (result.kind === 'fallback') {
            return (
                <Result
                    status="warning"
                    title="即时预订暂不可用，已转为询价"
                    subTitle={
                        <div>
                            预订渠道暂时无法响应，您的请求已记录为询价(编号 {result.enquiryId})。请留意邮箱（包括垃圾邮件文件夹）或在“用车询价”页面查看进展。
                        </div>
                    }
                    extra={[
                        <Button type="primary" key="console" onClick={() => navigate('enquiries')}>
                            查看询价
                        </Button>,
                        <Button key="back" onClick={() => setResult(undefined)}>
                            返回
                        </Button>,
                    ]}
                />
            );
        }

        if (result.kind === 'not_provisioned') {
            return (
                <Result
                    status="info"
                    title="即时预订未开通"
                    subTitle="您的账号尚未开通即时用车预订功能。请联系AOT客服为您开通后再试。"
                    extra={[
                        <Button type="primary" key="back" onClick={() => setResult(undefined)}>
                            返回
                        </Button>,
                    ]}
                />
            );
        }

        if (result.kind === 'agency_not_enabled') {
            return (
                <Result
                    status="info"
                    title="即时预订未启用"
                    subTitle="您所在的旅行社尚未启用即时用车预订。请联系AOT客服了解详情。"
                    extra={[
                        <Button type="primary" key="back" onClick={() => setResult(undefined)}>
                            返回
                        </Button>,
                    ]}
                />
            );
        }

        if (result.kind === 'not_configured') {
            return (
                <Result
                    status="info"
                    title="即时预订暂不可用"
                    subTitle="即时预订功能目前暂不可用，请联系AOT客服或稍后再试。"
                    extra={[
                        <Button type="primary" key="back" onClick={() => setResult(undefined)}>
                            返回
                        </Button>,
                    ]}
                />
            );
        }

        return null;
    };

    if (result) {
        return <Success />;
    }

    if (enquiryResult) {
        return (
            <Result
                status="success"
                title="询价已提交"
                subTitle={
                    <div>
                        您的用车询价已记录(编号 {enquiryResult.enquiryId})。请留意邮箱（包括垃圾邮件文件夹）或在“用车询价”页面查看进展。
                    </div>
                }
                extra={[
                    <Button type="primary" key="console" onClick={() => navigate('enquiries')}>
                        查看询价
                    </Button>,
                    <Button key="back" onClick={() => setEnquiryResult(undefined)}>
                        返回
                    </Button>,
                ]}
            />
        );
    }

    const eligibilityHint = (() => {
        if (eligibility.kind === 'agent_not_provisioned') {
            return (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="您尚未开通即时用车预订"
                    description={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <span>提交后将作为询价处理。如需开通即时预订，请向管理员申请。</span>
                            <Button
                                type="primary"
                                size="small"
                                loading={requestingAccess}
                                disabled={accessRequested}
                                onClick={handleRequestAccess}
                            >
                                {accessRequested ? '已申请' : '申请开通'}
                            </Button>
                        </div>
                    }
                />
            );
        }
        if (eligibility.kind === 'agency_not_enabled' || eligibility.kind === 'not_configured') {
            return (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="即时用车预订未开通"
                    description="提交后将作为询价处理。"
                />
            );
        }
        if (eligibility.kind === 'error') {
            return (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="无法确认即时预订状态"
                    description="提交时将根据账户实际权限处理。"
                />
            );
        }
        return null;
    })();

    const commissionPercent = eligibility.kind === 'eligible'
        ? Math.round(eligibility.commissionRate * 100)
        : 0;

    return (
        <div>
            <div style={{ fontSize: 18, fontWeight: 500, padding: '16px 0' }}>用车(机场接送)</div>
            {eligibilityHint}
            <div>
                <TransferForm data={data} setData={setData} />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        type="primary"
                        size="large"
                        style={{ width: '160px' }}
                        onClick={handleSubmit}
                        loading={submitting}
                        disabled={eligibility.kind === 'loading'}
                    >
                        {submitButtonLabel}
                    </Button>
                </div>
            </div>
            <Modal
                title="确认提交"
                open={confirmOpen}
                onOk={handleConfirm}
                onCancel={() => setConfirmOpen(false)}
                centered
                okText={confirmOkLabel}
                cancelText="取消"
                okButtonProps={{ loading: submitting }}
                cancelButtonProps={{ disabled: submitting }}
            >
                <div style={{ lineHeight: 1.8 }}>
                    <div>
                        <strong>日期：</strong>
                        {data.date} {data.time}
                    </div>
                    <div>
                        <strong>起点：</strong>
                        {data.pickup}
                    </div>
                    <div>
                        <strong>终点：</strong>
                        {data.dropoff}
                    </div>
                    {data.hours && (
                        <div>
                            <strong>用时：</strong>
                            {data.hours} 小时
                        </div>
                    )}
                    <div>
                        <strong>乘客 / 行李：</strong>
                        {data.passengerCount} / {data.luggageCount}
                    </div>
                    {data.retDate && (
                        <div>
                            <strong>返程：</strong>
                            {data.retDate} {data.retTime}
                        </div>
                    )}
                    {isEligible && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                            <div>
                                <strong>本次预订返佣：</strong>订单总额的 {commissionPercent}%
                            </div>
                            <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
                                提交后将打开预订页面完成支付。如渠道暂时不可用，您的请求会自动转为询价。
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
            {fetching && <div>Loading...</div>}
            <h3>合约商家(节省 50% 车费)</h3>
            <div style={{ padding: '16px 0' }}>
                <ActivitiesGrid activities={transferActivties as never[]} isFromPrice={true} />
            </div>
        </div>
    );
}

export default TransferEnquiryHomePage;
